// @tennis-matches/services/trainerstunden/billing-statement-recipient-email
// Live-E-Mail des Rechnungsempfängers: club_people (SSOT) + Billing-Kontakt + trainerstunden_people.

import { mergeParentPortalProfileFromSsotRows } from "./parent-portal-profile-ssot.js";

const RECIPIENT_SSOT_SELECT =
  "id,first_name,last_name,email,phone,street,house_number,postal_code,city,country";

function __asStr(x) {
  return String(x ?? "").trim();
}

/**
 * @param {import('@supabase/supabase-js').SupabaseClient} supabase
 * @param {string} recipientId
 */
async function __loadRecipientSsotRows(supabase, recipientId) {
  const id = __asStr(recipientId);
  if (!supabase || !id) {
    return { cp: null, bc: null, tp: null };
  }
  const [cpR, bcR, tpR] = await Promise.all([
    supabase
      .from("club_people")
      .select(RECIPIENT_SSOT_SELECT)
      .eq("id", id)
      .maybeSingle(),
    supabase
      .from("trainerstunden_billing_contacts")
      .select(RECIPIENT_SSOT_SELECT)
      .eq("id", id)
      .maybeSingle(),
    supabase
      .from("trainerstunden_people")
      .select(RECIPIENT_SSOT_SELECT)
      .eq("id", id)
      .maybeSingle(),
  ]);
  return {
    cp: !cpR.error && cpR.data ? cpR.data : null,
    bc: !bcR.error && bcR.data ? bcR.data : null,
    tp: !tpR.error && tpR.data ? tpR.data : null,
  };
}

/**
 * @param {object|null|undefined} cp
 * @param {object|null|undefined} bc
 * @param {object|null|undefined} tp
 */
export function mergeBillingRecipientSsotRow(cp, bc, tp) {
  const merged = mergeParentPortalProfileFromSsotRows(cp, bc, tp);
  const id = __asStr(merged.id);
  if (!id) return null;
  return {
    id,
    first_name: merged.firstName,
    last_name: merged.lastName,
    email: merged.email,
    phone: merged.phone,
    street: merged.street,
    house_number: merged.houseNumber,
    postal_code: merged.postalCode,
    city: merged.city,
    country: merged.country,
  };
}

/**
 * @param {import('@supabase/supabase-js').SupabaseClient} supabase
 * @param {object|null|undefined} statement
 */
export async function loadBillingStatementRecipientSsotRow(
  supabase,
  statement,
) {
  const st = statement && typeof statement === "object" ? statement : {};
  const rt = __asStr(st.recipient_type);
  const id =
    rt === "contact" ? __asStr(st.billing_contact_id) : __asStr(st.person_id);
  if (!id) return null;
  const rows = await __loadRecipientSsotRows(supabase, id);
  return mergeBillingRecipientSsotRow(rows.cp, rows.bc, rows.tp);
}

/**
 * @param {import('@supabase/supabase-js').SupabaseClient} supabase
 * @param {object|null|undefined} statement
 * @param {{ snapshotEmail?: string }} [opts]
 */
export async function resolveBillingStatementRecipientEmailLive(
  supabase,
  statement,
  opts = {},
) {
  const snap = __asStr(
    opts.snapshotEmail ?? statement?.recipient_email_snapshot,
  );
  const row = await loadBillingStatementRecipientSsotRow(supabase, statement);
  return __asStr(row?.email) || snap;
}
